import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { env } from './env';
import { queryClient } from './lib/queryClient';
import './styles/globals.css';
import App from './App';

// NOTE: StrictMode intentionally omitted. The app composes GSAP ScrollTrigger
// (pin-spacer reparenting), Lenis (html/body mutation), and Framer Motion
// AnimatePresence — all of which use imperative DOM manipulation. Under
// React 19, StrictMode's deliberate double-mount races with those effects'
// cleanup phase and produces NotFoundError: insertBefore/removeChild crashes
// on the very first render. StrictMode is a development-only feature with
// zero effect on production builds, so removing it costs nothing at runtime.

async function enableMockingIfRequested(): Promise<void> {
  if (!env.VITE_ENABLE_MSW) return;
  // Removed production block to allow mock service worker in Vercel demo build if VITE_ENABLE_MSW=true
  const { worker } = await import('./mocks/browser');
  await worker.start({
    onUnhandledRequest: 'bypass',
    serviceWorker: { url: '/mockServiceWorker.js' },
  });
  // eslint-disable-next-line no-console
  console.info('[MSW] Mock service worker active — backend bypassed.');
}

const rootEl = document.getElementById('root');
if (!rootEl) {
  throw new Error('Root element #root not found in index.html');
}

/** Remove the SSR boot-loader spinner from index.html once React has rendered. */
function removeBootLoader(): void {
  const boot = document.querySelector('.qxk-boot');
  if (boot && boot.parentNode) boot.parentNode.removeChild(boot);
}

void enableMockingIfRequested().then(() => {
  createRoot(rootEl).render(
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <App />
      </BrowserRouter>
    </QueryClientProvider>
  );
  // Allow one paint of the real UI before yanking the loader to avoid a flash.
  requestAnimationFrame(() => requestAnimationFrame(removeBootLoader));
});
